import React from 'react';
import clsx from 'clsx';
import { Info as InfoIcon } from 'react-feather';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Typography } from '@material-ui/core';
import { JupiterTheme, ExpandableText } from '@jupiterone/jupiter-web-toolkit';

const ICON_SIZE = 25;

const useStyles = makeStyles<JupiterTheme, Partial<SummaryCardProps>>((theme) =>
  createStyles({
    root: {
      width: '100%',
      padding: theme.spacing(0.5),
      paddingBottom: theme.spacing(1),
      display: 'grid',
      gridTemplateColumns: '0.125fr 1fr 1fr 0.25fr',
      gridTemplateRows: '0.25fr 0.5fr 1fr 1fr',
      gridTemplateAreas: `
      ".        category subtext subtext"
      "icon     title    title     ."
      ".        message  message   ."
      ".        message  message   ."`,
      color: 'white',
      backgroundColor: theme.palette.grey[800],
      cursor: ({ onClick }) => (onClick ? 'pointer' : 'default'),
      '&:hover > h6': {
        textDecoration: ({ onClick }) => (onClick ? 'underline' : 'none'),
      },
      '&:hover': {
        backgroundColor: ({ onClick }) =>
          onClick ? theme.palette.background.main : theme.palette.grey[800],
      },
    },
    subtext: {
      gridArea: 'subtext',
      fontSize: '0.675rem',
      textAlign: 'right',
      color: theme.palette.grey[500],
    },
    iconContainer: {
      gridArea: 'icon',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingRight: '10px',
      paddingLeft: '7px',
    },
    icon: {
      margin: 0,
      color: theme.palette.info.main,
    },
    title: {
      gridArea: 'title',
      fontSize: '1.25rem',
      fontWeight: 'bold',
    },
    category: {
      gridArea: 'category',
      fontSize: '0.75rem',
      color: theme.palette.grey[500],
      textTransform: 'uppercase',
    },
    messageContainer: { gridArea: 'message' },
    message: { fontSize: '0.875rem' },
    maxLinesMessage: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      '-webkit-line-clamp': ({ maxLines }) => maxLines,
      '-webkit-box-orient': 'vertical',
    },
  })
);

export interface SummaryCardProps {
  category?: string;
  title: string;
  message: string;
  onClick?: () => void;
  subtext?: string;
  id?: string;
  icon?: JSX.Element;
  isExpandable?: boolean;
  maxLines?: number;
  className?: string;
}
const SummaryCard = ({
  id,
  category,
  title,
  message,
  subtext,
  icon,
  className,
  isExpandable = false,
  maxLines = 3,
  onClick,
}: SummaryCardProps) => {
  const classes = useStyles({ onClick, maxLines });
  return (
    <>
      <div
        onClick={onClick}
        data-testid={id}
        className={clsx(classes.root, className)}
      >
        <div className={classes.subtext}>{subtext}</div>
        <div className={classes.iconContainer}>
          {icon ? icon : <InfoIcon className={classes.icon} size={ICON_SIZE} />}
        </div>
        <Typography variant="h6" className={classes.title}>
          {title}
        </Typography>
        {category ? <div className={classes.category}>{category}</div> : null}
        <div className={classes.messageContainer}>
          {isExpandable ? (
            <ExpandableText
              maxLines={maxLines}
              id={id || message}
              text={message}
              className={classes.message}
            />
          ) : (
            <Typography
              className={clsx(classes.message, classes.maxLinesMessage)}
            >
              {message}
            </Typography>
          )}
        </div>
      </div>
    </>
  );
};

export default SummaryCard;
